import { useTranslation } from "react-i18next";

/* ──────────────────────────────────────────────────────────────────
 * TaxonomyMemberTable — the member table shared by both taxonomy tabs.
 * Plain props only: it knows nothing about families, pipeline groups or
 * object-presence groups, so both tabs can pick the columns they need.
 * ────────────────────────────────────────────────────────────────── */

export type TaxonomyMemberColumn = "color" | "aliases" | "description";

export interface TaxonomyMemberRow {
  canonical: string;
  display_name: string;
  color?: string;
  aliases?: string[];
  description?: string;
}

type ColumnLabelKey =
  | "memberTable.color"
  | "memberTable.aliases"
  | "memberTable.description";

const COLUMN_LABEL_KEYS: Record<TaxonomyMemberColumn, ColumnLabelKey> = {
  color: "memberTable.color",
  aliases: "memberTable.aliases",
  description: "memberTable.description",
};

interface TaxonomyMemberTableProps {
  members: TaxonomyMemberRow[];
  /** Render order as indexes into `members`; defaults to the API order. */
  order?: number[];
  /** Optional columns, in render order. Display name and canonical are always shown. */
  columns?: TaxonomyMemberColumn[];
}

export const TaxonomyMemberTable = ({
  members,
  order,
  columns = ["color", "aliases"],
}: TaxonomyMemberTableProps) => {
  const { t } = useTranslation("settings");
  const rows = order ?? members.map((_, index) => index);

  return (
    <table className="w-full text-xs">
      <thead>
        <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground">
          <th className="font-semibold pb-1.5">{t("memberTable.displayName")}</th>
          <th className="font-semibold pb-1.5">{t("memberTable.canonical")}</th>
          {columns.map((column) => (
            <th key={column} className="font-semibold pb-1.5">
              {t(COLUMN_LABEL_KEYS[column])}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((index) => {
          const member = members[index];
          if (!member) return null;
          return (
            <tr key={member.canonical} className="border-t border-border/40">
              <td className="py-1.5 font-medium">{member.display_name}</td>
              <td className="py-1.5 font-mono text-muted-foreground">
                {member.canonical}
              </td>
              {columns.map((column) => {
                if (column === "color") {
                  return (
                    <td key={column} className="py-1.5">
                      {member.color ? (
                        <span className="inline-flex items-center gap-1.5">
                          <span
                            className="h-3 w-3 rounded-sm border border-border/60 shrink-0"
                            style={{ backgroundColor: member.color }}
                            title={member.color}
                          />
                          <span className="font-mono text-[10px] text-muted-foreground">
                            {member.color}
                          </span>
                        </span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                  );
                }
                if (column === "aliases") {
                  return (
                    <td key={column} className="py-1.5 text-muted-foreground">
                      {member.aliases && member.aliases.length > 0
                        ? member.aliases.join(", ")
                        : "—"}
                    </td>
                  );
                }
                return (
                  <td key={column} className="py-1.5 text-muted-foreground">
                    {member.description || "—"}
                  </td>
                );
              })}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

export default TaxonomyMemberTable;
